/**
 * Picking a dashboard theme.
 *
 * Each option is a small dashboard drawn in that theme's own colours — a
 * sidebar, a banner, three lit tiles — so the choice is made by looking, not
 * by reading a name. Picking one applies it to the whole app at once (with a
 * half-second cross-fade) and saves it to the account.
 */

import { Badge, Icon, Panel, SectionTitle } from '../../components/common';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n/I18nProvider';
import { THEMES } from '../../domain/themes';

function MiniDashboard({ swatch }) {
  const { bg, panel, accent, accent2, text, muted } = swatch;
  return (
    <div className="theme-mini" style={{ background: bg }} aria-hidden="true">
      <div className="theme-mini__side" style={{ background: panel }}>
        <span style={{ background: accent }} />
        <span style={{ background: muted, opacity: 0.45 }} />
        <span style={{ background: muted, opacity: 0.45 }} />
        <span style={{ background: muted, opacity: 0.45 }} />
      </div>
      <div className="theme-mini__main">
        <div
          className="theme-mini__hero"
          style={{ background: `linear-gradient(135deg, ${panel}, ${bg})`, borderColor: `${accent}44` }}
        >
          <span className="theme-mini__dot" style={{ background: `linear-gradient(145deg, ${accent}, ${accent2})`, boxShadow: `0 0 10px ${accent}` }} />
          <span className="theme-mini__line" style={{ background: text, opacity: 0.85 }} />
        </div>
        <div className="theme-mini__tiles">
          {[accent, accent2, accent].map((c, i) => (
            <span key={i} style={{ background: panel, borderColor: `${c}55` }}>
              <i style={{ background: `linear-gradient(145deg, ${c}, ${c}aa)`, boxShadow: `0 0 8px ${c}88` }} />
            </span>
          ))}
        </div>
        <div className="theme-mini__rows">
          <span style={{ background: muted, opacity: 0.35 }} />
          <span style={{ background: muted, opacity: 0.25 }} />
        </div>
      </div>
    </div>
  );
}

export function ThemePicker() {
  const { theme, setTheme, saving } = useTheme();
  // The theme names are the platform's; tr() reads them in English when the page is.
  const { tr } = useI18n();

  return (
    <Panel>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle icon="palette" className="!mb-0">
          {tr('مظهر لوحة التحكم')}
        </SectionTitle>
        {saving && (
          <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200" icon="loader-2">
            {tr('جارٍ الحفظ في حسابك')}
          </Badge>
        )}
      </div>
      <p className="mb-5 text-sm text-slate-400">{tr('اختر الألوان التي ترتاح لها عينك — يُطبَّق المظهر فوراً ويُحفظ في حسابك على أي جهاز.')}</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" role="radiogroup" aria-label={tr('مظهر لوحة التحكم')}>
        {THEMES.map((t, i) => {
          const active = t.id === theme;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setTheme(t.id)}
              className={`theme-card ${active ? 'theme-card--active' : ''}`}
              style={{ '--theme-accent': t.swatch.accent, animationDelay: `${i * 60}ms` }}
            >
              <MiniDashboard swatch={t.swatch} />
              <div className="flex items-start gap-3 p-3 text-start">
                <span
                  className="theme-card__radio"
                  style={{ borderColor: t.swatch.accent, background: active ? t.swatch.accent : 'transparent' }}
                >
                  {active && <Icon name="check" size={13} className="text-onaccent" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="font-bold text-white">{tr(t.name)}</span>
                    <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
                      {tr(t.mode === 'light' ? 'فاتح' : 'داكن')}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-400">{tr(t.hint)}</span>
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

export default ThemePicker;
